import React, { useRef, useLayoutEffect, useState, useMemo, useCallback } from 'react';
import { useFlight } from '../contexts/FlightContext';
import { STATIONS, Station, TimestampEntry, FlightData, ARRIVAL_STATIONS, ArrivalStation } from '../types';
import { CheckIcon } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';

const getLatestTimestamp = (flightData: FlightData | null, station: Station, type: 'crew' | 'pilots'): TimestampEntry | null => {
    const entries = flightData?.crewMovement[station]?.[type];
    if (!entries || entries.length === 0) return null;
    return entries[entries.length - 1];
};

const ArrivalProgressBar: React.FC = () => {
    const { selectedFlightData: flightData } = useFlight();

    const getLatestArrivalTimestamp = useCallback((station: ArrivalStation): TimestampEntry | null => {
        const entries = flightData?.arrivalMovement?.[station]?.events;
        return entries && entries.length > 0 ? entries[entries.length - 1] : null;
    }, [flightData]);

    const lastCompletedStationIndex = useMemo(() => {
        if (!flightData?.isReturnFlight) return -1;
        let lastConsecutiveIndex = -1;
        for (let i = 0; i < ARRIVAL_STATIONS.length; i++) {
            if (getLatestArrivalTimestamp(ARRIVAL_STATIONS[i])) {
                lastConsecutiveIndex = i;
            } else {
                // The sequence is broken, stop here.
                break;
            }
        }
        return lastConsecutiveIndex;
    }, [getLatestArrivalTimestamp, flightData?.isReturnFlight]);
    
    if (!flightData || !flightData.isReturnFlight) return null;

    const wheelchairCount = flightData.arrivalMovement?.wheelchairs || '0';

    return (
        <div className="p-4 bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg">
         <h3 className="text-lg font-bold text-orange-400 border-b border-slate-700 pb-2 mb-4">ARRIVAL PROGRESS BAR</h3>
         <div className="w-full overflow-x-auto py-8 -mx-4 px-4">
            <div className="relative mt-4">
                <div className="flex items-start" style={{ minWidth: `${ARRIVAL_STATIONS.length * 120}px` }}>
                    {ARRIVAL_STATIONS.map((station, index) => {
                        const timestamp = getLatestArrivalTimestamp(station);
                        const isCompleted = index <= lastCompletedStationIndex;
                        const isCurrent = lastCompletedStationIndex === index - 1;

                        const nodeClasses = isCompleted
                            ? 'bg-gradient-to-br from-orange-500 to-orange-600 border-orange-400'
                            : 'bg-slate-700 border-slate-600';
                        if (isCurrent) nodeClasses.concat(' animate-glow');

                        const connectorClasses = index <= lastCompletedStationIndex
                            ? 'bg-gradient-to-r from-orange-500 to-orange-600'
                            : 'bg-slate-700';

                        return (
                            <React.Fragment key={station}>
                                <div className="relative group flex flex-col items-center w-28 text-center flex-shrink-0">
                                    <div className="relative z-10">
                                        <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-colors duration-500 ${nodeClasses}`}>
                                            {isCompleted && <CheckIcon className="w-5 h-5 text-white" />}
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <h4 className="font-semibold text-xs text-slate-200 leading-tight h-10 flex items-center justify-center">{station}</h4>
                                        <div className="mt-1 text-xs space-y-0.5 min-h-[15px]">
                                            <span className="text-slate-300 font-mono">{timestamp ? new Date(timestamp.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit'}) : '--:--'}</span>
                                        </div>
                                    </div>
                                </div>
                                {index < ARRIVAL_STATIONS.length - 1 && (
                                    <div className="flex-1 pt-[19px]">
                                        <div className={`h-1.5 w-full transition-colors duration-500 ${connectorClasses}`}></div>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
         </div>
         <div className="mt-4 pt-4 border-t border-slate-700 flex justify-center items-center gap-4">
            <span className="text-lg font-bold text-orange-400">Wheelchairs:</span>
            <span className="text-2xl font-bold text-white bg-slate-800/50 border border-slate-600 rounded-md py-1 px-4">
                {wheelchairCount}
            </span>
        </div>
       </div>
    );
};

const ProgressBar: React.FC = () => {
    const { selectedFlightData: flightData } = useFlight();
    const { appAssets, defaultAppAssets } = useAuth();

    const containerRef = useRef<HTMLDivElement>(null);
    const nodeRefs = useRef<(HTMLDivElement | null)[]>([]);
    
    const [iconPositions, setIconPositions] = useState<{
        crewLeft: number | null;
        pilotLeft: number | null;
        passengersLeft: number | null;
        showPassengersIcon: boolean;
        crewTop: number;
        pilotTop: number;
        passengerTop: number;
    }>({ 
        crewLeft: null, 
        pilotLeft: null, 
        passengersLeft: null, 
        showPassengersIcon: false,
        crewTop: -32,
        pilotTop: 0,
        passengerTop: -32,
     });

    const showCrewIcon = useMemo(() => {
        if (!flightData) return false;
        return STATIONS.some(station => flightData.crewMovement[station]?.crew?.length > 0)
    }, [flightData]);

    const showPilotIcon = useMemo(() => {
        if (!flightData) return false;
        return STATIONS.some(station => flightData.crewMovement[station]?.pilots?.length > 0)
    }, [flightData]);

    const findLastStationIndex = useCallback((type: 'crew' | 'pilots'): number => {
        if (!flightData) return -1;
        let lastIndex = -1;
        let latestTime = 0;

        STATIONS.forEach((station, index) => {
            const entries = flightData.crewMovement[station]?.[type];
            if (entries && entries.length > 0) {
                const lastEntry = entries[entries.length - 1];
                const entryTime = new Date(lastEntry.time).getTime();
                if (entryTime > latestTime) {
                    latestTime = entryTime;
                    lastIndex = index;
                }
            }
        });

        return lastIndex;
    }, [flightData]);

    useLayoutEffect(() => {
        if (!flightData) return;

        // 1. Define key station indices
        const atAircraftIndex = STATIONS.indexOf('At Aircraft');

        // 2. Find ACTUAL last station index for CREW and PILOT
        const actualLastCrewIndex = findLastStationIndex('crew');
        const actualLastPilotIndex = findLastStationIndex('pilots');
        
        // 3. Determine VISUAL last station index for CREW and PILOT (both stop at 'At Aircraft')
        const visualLastCrewIndex = actualLastCrewIndex === -1 ? -1 : Math.min(actualLastCrewIndex, atAircraftIndex);
        const visualLastPilotIndex = actualLastPilotIndex === -1 ? -1 : Math.min(actualLastPilotIndex, atAircraftIndex);


        // 4. Determine Passenger Icon visibility and position based on ACTUAL crew progress
        const readyForBoardingIndex = STATIONS.indexOf('Ready For Boarding');
        const pushBackIndex = STATIONS.indexOf('Push Back');
        const passengerActivityStations = STATIONS.slice(readyForBoardingIndex, pushBackIndex + 1);

        const hasPassengerActivity = passengerActivityStations.some(station => flightData.crewMovement[station]?.crew?.length > 0);
        const isPushBackComplete = !!flightData.crewMovement['Push Back']?.crew?.length;
        const showPassengersIcon = hasPassengerActivity && !isPushBackComplete;

        let passengerPositionIndex = -1;
        if (showPassengersIcon) {
            // Passenger icon tracks the crew's progress during the boarding/departure phase
            passengerPositionIndex = actualLastCrewIndex;
        }

        const container = containerRef.current;
        if (!container || !nodeRefs.current[0]) return; // Guard against missing refs

        // Helper function to calculate the left offset
        const calculateLeft = (index: number): number | null => {
            if (index === -1) {
                const firstNode = nodeRefs.current[0];
                // Position before the first node
                return firstNode ? firstNode.offsetLeft - 16 : null;
            }
            const node = nodeRefs.current[index];
            return node ? node.offsetLeft + (node.offsetWidth / 2) : null;
        };
        
        const crewLeftPos = calculateLeft(visualLastCrewIndex);
        const pilotLeftPos = calculateLeft(visualLastPilotIndex);
        const passengerLeftPos = calculateLeft(passengerPositionIndex);
        
        // Default vertical positions
        let crewTop = -32;
        let pilotTop = 0;
        let passengerTop = -32;

        // De-conflict icons if they are at the same station
        if (visualLastCrewIndex === visualLastPilotIndex && visualLastCrewIndex !== -1) {
            pilotTop = 8; // Move pilot icon down
        }

        if (passengerPositionIndex === visualLastCrewIndex && passengerPositionIndex !== -1) {
           passengerTop = -40; // Move passenger icon up
           crewTop = -24;      // Move crew icon down
        }

        setIconPositions({
            crewLeft: crewLeftPos,
            pilotLeft: pilotLeftPos,
            passengersLeft: passengerLeftPos,
            showPassengersIcon: showPassengersIcon,
            crewTop,
            pilotTop,
            passengerTop,
        });

    }, [flightData, findLastStationIndex]);

    if (!flightData) return null;

    const isReadyForBoarding = !!getLatestTimestamp(flightData, 'Ready For Boarding', 'crew');
    const readyForBoardingIndex = STATIONS.indexOf('Ready For Boarding');
    const lastCrewIndex = findLastStationIndex('crew');

    return (
        <div className="w-full overflow-x-auto py-8 -mx-4 px-4">
             <div className="relative mt-4" ref={containerRef}>
                {showCrewIcon && (appAssets.crewIcon || defaultAppAssets.crewIcon) && iconPositions.crewLeft !== null && (
                     <img 
                        src={appAssets.crewIcon || defaultAppAssets.crewIcon}
                        alt="Crew"
                        className="absolute z-20 w-8 h-8 rounded-full bg-blue-600 p-1 border-2 border-white shadow-lg"
                        style={{
                            left: `${iconPositions.crewLeft}px`,
                            top: `${iconPositions.crewTop}px`,
                            transform: 'translateX(-50%)',
                            transition: 'left 0.5s ease-in-out, top 0.5s ease-in-out',
                        }}
                     />
                )}
                
                {showPilotIcon && (appAssets.pilotIcon || defaultAppAssets.pilotIcon) && iconPositions.pilotLeft !== null && (
                     <img 
                        src={appAssets.pilotIcon || defaultAppAssets.pilotIcon}
                        alt="Pilot"
                        className="absolute z-20 w-8 h-8 rounded-full bg-amber-500 p-1 border-2 border-white shadow-lg"
                        style={{
                            left: `${iconPositions.pilotLeft}px`,
                            top: `${iconPositions.pilotTop}px`,
                            transform: 'translateX(-50%)',
                            transition: 'left 0.5s ease-in-out, top 0.5s ease-in-out',
                        }}
                     />
                )}
                
                {(appAssets.passengersIcon || defaultAppAssets.passengersIcon) && iconPositions.showPassengersIcon && iconPositions.passengersLeft !== null && (
                     <img 
                        src={appAssets.passengersIcon || defaultAppAssets.passengersIcon}
                        alt="Passengers"
                        className="absolute z-20 w-8 h-8 rounded-full bg-green-500 p-1 border-2 border-white shadow-lg animate-vibrate"
                        style={{
                            left: `${iconPositions.passengersLeft}px`,
                            top: `${iconPositions.passengerTop}px`,
                            transform: 'translateX(-50%)',
                            transition: 'left 0.5s ease-in-out, top 0.5s ease-in-out',
                        }}
                     />
                )}

                <div className="flex items-start" style={{ minWidth: `${STATIONS.length * 120}px` }}>
                    {STATIONS.map((station, index) => {
                        const crewTime = getLatestTimestamp(flightData, station, 'crew');
                        const pilotTime = getLatestTimestamp(flightData, station, 'pilots');
                        
                        const isCompleted = !!crewTime;
                        const isCurrent = index === lastCrewIndex + 1;

                        const showPilotInfo = STATIONS.indexOf(station) <= STATIONS.indexOf('At Aircraft');

                        let nodeClasses = 'bg-slate-700 border-slate-600';
                        if (isReadyForBoarding && index >= readyForBoardingIndex && isCompleted) {
                            nodeClasses = 'bg-green-500 border-green-400';
                        } else if (isCompleted) {
                            nodeClasses = 'bg-gradient-to-br from-blue-500 to-cyan-500 border-blue-400';
                        }
                        if (isCurrent) nodeClasses = 'bg-gradient-to-br from-indigo-500 to-purple-500 border-indigo-400 animate-glow';

                        const connectorCompleted = isCompleted;
                        let connectorClasses = 'bg-slate-700';
                         if (isReadyForBoarding && index >= readyForBoardingIndex -1 && connectorCompleted) {
                            connectorClasses = 'bg-green-500';
                        } else if (connectorCompleted) {
                            connectorClasses = 'bg-gradient-to-r from-blue-500 to-cyan-500';
                        }

                        return (
                            <React.Fragment key={station}>
                                <div 
                                    className="relative group flex flex-col items-center w-28 text-center flex-shrink-0"
                                    ref={el => { nodeRefs.current[index] = el; }}
                                >
                                    <div className="relative z-10">
                                        <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-colors duration-500 ${nodeClasses}`}>
                                            {(crewTime || pilotTime) && <CheckIcon className="w-5 h-5 text-white" />}
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <h4 className="font-semibold text-xs text-slate-200 leading-tight h-10 flex items-center justify-center">{station}</h4>
                                        <div className="mt-1 text-[10px] space-y-0.5 min-h-[30px] flex flex-col justify-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <span className={`font-bold ${crewTime ? 'text-blue-400' : 'text-slate-500'}`}>C:</span>
                                                <span className="text-slate-300 font-mono">{crewTime ? new Date(crewTime.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit'}) : '--:--'}</span>
                                            </div>
                                            {showPilotInfo && (
                                                <div className="flex items-center justify-center gap-1">
                                                    <span className={`font-bold ${pilotTime ? 'text-amber-400' : 'text-slate-500'}`}>P:</span>
                                                    <span className="text-slate-300 font-mono">{pilotTime ? new Date(pilotTime.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit'}) : '--:--'}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {/* Tooltip */}
                                    <div className="absolute bottom-full mb-3 w-56 p-2 text-xs text-left bg-slate-900 border border-slate-700 rounded-md shadow-lg hidden group-hover:block z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <p className="font-bold text-slate-100 border-b border-slate-600 pb-1 mb-1">{station}</p>
                                        <div className="space-y-1">
                                        <p className="text-slate-400"><strong>Crew:</strong> {crewTime ? `${new Date(crewTime.time).toLocaleString('en-GB')} by ${crewTime.by}` : 'N/A'}</p>
                                        {showPilotInfo && <p className="text-slate-400"><strong>Pilots:</strong> {pilotTime ? `${new Date(pilotTime.time).toLocaleString('en-GB')} by ${pilotTime.by}` : 'N/A'}</p>}
                                        </div>
                                    </div>
                                </div>
                                {index < STATIONS.length - 1 && (
                                    <div className="flex-1 pt-[19px]">
                                        <div className={`h-1.5 w-full transition-colors duration-500 ${connectorClasses}`}></div>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const OverviewScreen: React.FC = () => {
  const { selectedFlightData: flightData } = useFlight();
  if (!flightData) return null;
  const { counterDistribution, immigrationApproved, immigrationComment, atsr } = flightData;
  const bookingSafe = counterDistribution.booking || { c: '', m: '', inf: '', totalPassengers: '0' };
  const etdTime = counterDistribution.etd ? new Date(counterDistribution.etd).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'N/A';
  const finalFiguresSafe = counterDistribution.finalFigures || { c: '', m: '', inf: '', totalPassengers: '0', 'Total Bags': '', 'Crew Bags': '', 'Waved Bags': '', 'Wheel Chairs': '', 'Buggy': '', 'Final Fuel': '' };
  const scannedPassengersCount = flightData.scannedPassengers?.length || 0;

  const getTimestampText = (station: Station, types: ('crew' | 'pilots')[] = ['crew', 'pilots']): string | null => {
    const crewMovement = flightData.crewMovement;
    if (!crewMovement || !crewMovement[station]) return null;

    const entries = types.flatMap(type => crewMovement[station]?.[type] || []);
    if (entries.length === 0) return null;
    
    const latestEntry = entries.reduce((latest, current) => 
        new Date(current.time).getTime() > new Date(latest.time).getTime() ? current : latest
    );
    
    return new Date(latestEntry.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };
    
  const crewArrivedTime = getTimestampText('Crew Arrived');
  const firstPaxInTime = getTimestampText('1st Pax IN', ['crew']);
  const doorsClosedTime = getTimestampText('Doors Closed', ['crew']);
  const finalizedTime = flightData.finalizeTimestamp ? new Date(flightData.finalizeTimestamp.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : null;
    
  const milestones = [
      { label: "Crew Arrived", time: crewArrivedTime, color: 'text-amber-400' },
      { label: "Boarding Started", time: firstPaxInTime, color: 'text-green-400' },
      { label: "Doors Closed", time: doorsClosedTime, color: 'text-cyan-400' },
      { label: "Finalized", time: finalizedTime, color: 'text-purple-400' }
  ];

  const renderSection = (title: string, data: { [key: string]: string }) => (
    <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
      <h3 className="text-lg font-bold text-blue-400 border-b border-slate-700 pb-2 mb-3">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-sm">
        {Object.entries(data).map(([key, value]) => (
          <div key={key}>
            <span className="text-slate-400">{key}:</span>
            <span className="ml-2 font-semibold text-slate-100">{value || 'N/A'}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const finalFiguresForDisplay = {
      'Total Passengers': finalFiguresSafe.totalPassengers,
      'C': finalFiguresSafe.c,
      'M': finalFiguresSafe.m,
      'Inf': finalFiguresSafe.inf,
      'Total Bags': finalFiguresSafe['Total Bags'],
      'Crew Bags': finalFiguresSafe['Crew Bags'],
      'Waved Bags': finalFiguresSafe['Waved Bags'],
      'Wheel Chairs': finalFiguresSafe['Wheel Chairs'],
      'Buggy': finalFiguresSafe['Buggy'],
  };

  return (
    <div className="space-y-6">
      <ArrivalProgressBar />
      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <div className="flex flex-wrap justify-between items-start gap-4">
            {/* Immigration & Comments */}
            <div className="flex-1 min-w-[250px]">
                {immigrationApproved ? (
                    <div className="mb-2">
                        <h2 className="text-xl font-bold text-green-400 animate-pulse">IMMIGRATION APPROVED</h2>
                        {immigrationComment && (
                            <p className="text-slate-300 mt-1 text-sm italic">"{immigrationComment}"</p>
                        )}
                    </div>
                ) : (
                    <div className="mb-2">
                        <h2 className="text-xl font-bold text-amber-400">IMMIGRATION PENDING</h2>
                    </div>
                )}
                
                <div className="flex flex-wrap items-baseline text-sm border-t border-slate-700 pt-2 divide-x divide-slate-600 -mx-2 sm:-mx-4">
                    <div className="px-2 sm:px-4 py-1">
                        <span className="text-slate-400">ETD:</span>
                        <span className="ml-1 font-bold font-mono text-amber-300 text-base">{etdTime}</span>
                    </div>
                    
                    <div className="px-2 sm:px-4 py-1">
                        {/* Mobile View */}
                        <span className="sm:hidden">
                            <span className="text-slate-400">C/M/I:</span>
                            <span className="font-semibold font-mono text-white ml-1">
                                {bookingSafe.c || '0'}/{bookingSafe.m || '0'}/{bookingSafe.inf || '0'}
                            </span>
                        </span>
                        {/* Desktop View */}
                        <span className="hidden sm:inline-flex items-center gap-x-3">
                            <span className="text-slate-400">Booking:</span>
                            <span className="font-semibold text-white">C:<span className="font-mono ml-1">{bookingSafe.c || '0'}</span></span>
                            <span className="font-semibold text-white">M:<span className="font-mono ml-1">{bookingSafe.m || '0'}</span></span>
                            <span className="font-semibold text-white">INF:<span className="font-mono ml-1">{bookingSafe.inf || '0'}</span></span>
                        </span>
                    </div>

                    <div className="px-2 sm:px-4 py-1">
                        <span className="text-slate-400">Total:</span>
                        <span className="ml-1 font-bold text-white text-base">{bookingSafe.totalPassengers || '0'} PAX</span>
                    </div>
                    <div className="px-2 sm:px-4 py-1">
                        <span className="text-slate-400">Scanned:</span>
                        <span className="ml-1 font-bold text-green-400 text-base">{scannedPassengersCount} PAX</span>
                    </div>
                    {finalFiguresSafe['Final Fuel'] && (
                        <div className="px-2 sm:px-4 py-1">
                            <span className="text-slate-400">Final Fuel ⛽️:</span>
                            <span className="ml-1 font-bold text-red-400 text-base">{finalFiguresSafe['Final Fuel']}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Key Milestones */}
            <div className="flex-shrink-0">
                <div className="flex flex-col items-start sm:items-end space-y-1 text-sm">
                    {milestones.map(m => (
                        <div key={m.label} className="flex items-center gap-3">
                            <span className="text-slate-400">{m.label}:</span>
                            <span className={`font-bold font-mono text-lg ${m.time ? m.color : 'text-slate-600'}`}>
                                {m.time || '--:--'}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
      </div>
       
       <div className="p-4 bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg">
         <h3 className="text-lg font-bold text-blue-400 border-b border-slate-700 pb-2 mb-4">Stations Progress</h3>
         <ProgressBar />
       </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderSection('Counter Distribution', counterDistribution.counters)}
        {renderSection('Gate Assignments', counterDistribution.gates)}
        {renderSection('ATSR', atsr)}
        {renderSection('Final Figures', finalFiguresForDisplay)}
      </div>
    </div>
  );
};

export default OverviewScreen;